import React, { useState, useEffect, Suspense, useCallback } from 'react';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './services/auth';
import { ThemeProvider } from './components/ThemeProvider';
import { ModalProvider, useModal } from './components/ModalProvider';
import useIncidentSocket from './hooks/useIncidentSocket';
import { LogoContext, pages } from './data/menuStructure';
import MainLayout from './components/MainLayout';
import CommandPalette from './components/CommandPalette';
import FloatingChatWidget from './components/chat/FloatingChatWidget';
import { pushRecent, getRecent, getSidebarCollapsed, setSidebarCollapsed as persistSidebarCollapsed } from './data/storage';
import Login from './pages/Login';
import NOC from './pages/NOC';
import ServerDetail from './pages/ServerDetail';
import DeviceDetail from './pages/DeviceDetail';
import './index.css';
import * as Sentry from '@sentry/react';

const PageLoader = () => (
  <div className="flex" style={{ alignItems: 'center', justifyContent: 'center', height: 256 }}>
    <div className="flex" style={{ alignItems: 'center', gap: 12, color: 'var(--text-2)' }}>
      <svg className="animate-spin" width="20" height="20" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
      </svg>
      Loading page...
    </div>
  </div>
);

class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { try { Sentry.captureException(error, { extra: { componentStack: info?.componentStack } }); } catch (e) { /* reporting must never break UI */ } }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex" style={{ flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 256, textAlign: 'center' }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>⚠️</div>
          <div style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 16 }}>Page failed to load</div>
          <button onClick={() => { this.setState({ hasError: false }); window.location.reload(); }}
            style={{ padding: '8px 16px', borderRadius: 8, background: 'var(--accent-1)', color: '#fff', fontSize: 13, border: 'none', cursor: 'pointer' }}>
            Reload Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { refetchOnWindowFocus: false, retry: 1, staleTime: 0, gcTime: 30000 },
  },
});

function AppContent() {
  const { alert: showAlert } = useModal();
  const { user, loading: authLoading, logout } = useAuth();
  const [currentPage, setCurrentPage] = useState(() => { const h = window.location.hash.replace('#', ''); return h || 'dashboard'; });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => getSidebarCollapsed());
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);

  useIncidentSocket(user);

  useEffect(() => {
    fetch('/api/config/logo')
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.logo) setLogoUrl(d.logo); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (logoUrl) {
      let link = document.querySelector("link[rel~='icon']");
      if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
      link.href = logoUrl;
      link.type = 'image/svg+xml';
    }
  }, [logoUrl]);

  useEffect(() => {
    if (!authLoading && !user && currentPage !== 'login') { window.location.hash = 'login'; }
    if (!authLoading && user && currentPage === 'login') { window.location.hash = 'dashboard'; }
  }, [authLoading, user, currentPage]);

  useEffect(() => {
    const h = () => { const hash = window.location.hash.replace('#', ''); if (hash) setCurrentPage(hash); };
    window.addEventListener('hashchange', h);
    return () => window.removeEventListener('hashchange', h);
  }, []);

  useEffect(() => {
    const onSessionExpired = () => {
      showAlert('Session expired. Please log in again.', 'Session expired');
    };
    window.addEventListener('synoti_session_expired', onSessionExpired);
    return () => window.removeEventListener('synoti_session_expired', onSessionExpired);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setCommandPaletteOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const handlePaletteNavigate = useCallback((id, overrideHash) => {
    if (id === 'swagger') {
      const t = localStorage.getItem('synoti_access_token');
      if (t) document.cookie = `synoti_token=${t}; path=/; max-age=3600`;
      window.open('/docs', '_blank');
      return;
    }
    if (id === 'noc') { window.open('/noc-v2', '_blank'); return; }
    pushRecent(id);
    if (overrideHash) {
      const page = overrideHash.replace(/[?#].*/, '');
      setCurrentPage(page);
      window.location.hash = overrideHash;
    } else {
      setCurrentPage(id);
      window.location.hash = id;
    }
  }, []);

  const { data: incidentData } = useQuery({
    queryKey: ['incident-count'],
    queryFn: async () => {
      try {
        const res = await fetch('/api/incidents?exclude_status=resolved&exclude_event_type=vulnerability&exclude_event_type=wazuh_vulnerability&limit=1');
        const d = await res.json();
        return { total: d.total || 0, open: d.stats?.open || 0, investigating: d.stats?.investigating || 0, inProgress: d.stats?.in_progress || 0 };
      } catch { return { total: 0, open: 0, investigating: 0, inProgress: 0 }; }
    },
    refetchInterval: 30000,
    retry: false,
    enabled: !!user && currentPage !== 'login',
  });

  const { data: vulnStats } = useQuery({
    queryKey: ['vuln-count'],
    queryFn: async () => {
      try {
        const res = await fetch('/api/vulnerabilities?status=open&limit=1');
        const d = await res.json();
        return { critical: d.critical ?? 0, high: d.high ?? 0, others: (d.medium ?? 0) + (d.low ?? 0) };
      } catch { return { critical: 0, high: 0, others: 0 }; }
    },
    refetchInterval: 60000,
    retry: false,
    enabled: !!user && currentPage !== 'login',
  });

  const { data: workerStats } = useQuery({
    queryKey: ['worker-stats'],
    queryFn: async () => {
      try {
        const res = await fetch('/api/workers/stats');
        const d = await res.json();
        return {
          online: d.online_workers ?? 0,
          total: d.total_workers ?? 0,
          offline: d.offline_workers ?? 0,
          busy: d.busy_workers ?? 0,
          running: d.running_tasks ?? 0,
          pending: d.pending_tasks ?? 0,
          failed: d.failed_tasks ?? 0,
          completed: d.completed_tasks ?? 0,
        };
      } catch { return { online: 0, total: 0, offline: 0, busy: 0, running: 0, pending: 0, failed: 0, completed: 0 }; }
    },
    refetchInterval: 10000,
    retry: false,
    enabled: !!user && currentPage !== 'login',
  });

  const { data: execRunningCount } = useQuery({
    queryKey: ['execution-counts'],
    queryFn: async () => {
      try {
        const res = await fetch('/api/executions/counts');
        const d = await res.json();
        return (d.running || 0) + (d.pending || 0);
      } catch { return 0; }
    },
    refetchInterval: 15000,
    retry: false,
    enabled: !!user && currentPage !== 'login',
  });

  if (authLoading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: 'var(--bg-1, #0b0f14)' }}><div style={{ width: 32, height: 32, border: '4px solid var(--accent-1)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} /></div>;
  if (currentPage === 'login' || !user) return <Login />;

  const handleNavClick = (id) => {
    if (id === 'swagger') { const t = localStorage.getItem('synoti_access_token'); if (t) document.cookie = `synoti_token=${t}; path=/; max-age=3600`; window.open('/docs', '_blank'); return; }
    if (id === 'noc') { window.open('/noc-v2', '_blank'); return; }
    pushRecent(id);
    setCurrentPage(id); window.location.hash = id;
  };
  const handleLogout = () => { logout(); window.location.hash = 'login'; };
  const isNOC = currentPage === 'noc';
  const isServerDetail = currentPage.startsWith('servers/') && currentPage !== 'servers';
  const isDeviceDetail = currentPage.startsWith('devices/') && currentPage !== 'devices';

  const vulnCritical = vulnStats?.critical ?? 0;
  const vulnHigh = vulnStats?.high ?? 0;
  const vulnOthers = vulnStats?.others ?? 0;

  const sidebarProps = {
    currentPage, sidebarCollapsed, onNavClick: handleNavClick,
    onToggleCollapse: () => {
      const next = !sidebarCollapsed;
      setSidebarCollapsed(next);
      persistSidebarCollapsed(next);
    },
    incidentCount: incidentData?.total || 0,
    incidentOpen: incidentData?.open || 0,
    incidentInvestigating: incidentData?.investigating || 0,
    incidentInProgress: incidentData?.inProgress || 0,
    workerOnline: workerStats?.online ?? 0,
    workerTotal: workerStats?.total ?? 0,
    busyWorkers: workerStats?.busy ?? 0,
    pendingTasks: workerStats?.pending ?? 0,
    runningTasks: workerStats?.running ?? 0,
    failedTasks: workerStats?.failed ?? 0,
    execRunningCount: execRunningCount ?? 0,
    vulnCount: vulnCritical + vulnHigh + vulnOthers,
    vulnCritical, vulnHigh, vulnOthers,
    userRole: user?.role, logoUrl,
  };

  if (isDeviceDetail) {
    const deviceId = currentPage.split('/')[1];
    return (
      <LogoContext.Provider value={logoUrl}>
        <MainLayout
          {...sidebarProps}
          currentPage="devices"
          user={user}
          onLogout={handleLogout}
          logoUrl={logoUrl}
          onOpenPalette={() => setCommandPaletteOpen(true)}
        >
          <ErrorBoundary><Suspense fallback={<PageLoader />}><DeviceDetail /></Suspense></ErrorBoundary>
        </MainLayout>
        <CommandPalette isOpen={commandPaletteOpen} onClose={() => setCommandPaletteOpen(false)} onNavigate={handlePaletteNavigate} userRole={user?.role} recentIds={getRecent()} />
      </LogoContext.Provider>
    );
  }

  if (isServerDetail) {
    const serverId = currentPage.split('/')[1];
    return (
      <LogoContext.Provider value={logoUrl}>
        <MainLayout
          {...sidebarProps}
          currentPage={currentPage}
          user={user}
          onLogout={handleLogout}
          logoUrl={logoUrl}
          onOpenPalette={() => setCommandPaletteOpen(true)}
        >
          <ErrorBoundary><Suspense fallback={<PageLoader />}><ServerDetail serverId={serverId} /></Suspense></ErrorBoundary>
        </MainLayout>
        <CommandPalette isOpen={commandPaletteOpen} onClose={() => setCommandPaletteOpen(false)} onNavigate={handlePaletteNavigate} userRole={user?.role} recentIds={getRecent()} />
      </LogoContext.Provider>
    );
  }

  if (isNOC) return <ErrorBoundary><Suspense fallback={<PageLoader />}><NOC onBack={() => { window.location.hash = 'dashboard'; }} /></Suspense></ErrorBoundary>;

  const PageComponent = pages[currentPage.startsWith('entity-360') ? 'entity-360' : currentPage] || pages.dashboard;
  return (
    <LogoContext.Provider value={logoUrl}>
      <MainLayout
        {...sidebarProps}
        user={user}
        onLogout={handleLogout}
        logoUrl={logoUrl}
        onOpenPalette={() => setCommandPaletteOpen(true)}
      >
        <ErrorBoundary key={currentPage}>
          <Suspense fallback={<PageLoader />}>
            <PageComponent />
          </Suspense>
        </ErrorBoundary>
      </MainLayout>
      <CommandPalette isOpen={commandPaletteOpen} onClose={() => setCommandPaletteOpen(false)} onNavigate={handlePaletteNavigate} userRole={user?.role} recentIds={getRecent()} />
      <FloatingChatWidget />
    </LogoContext.Provider>
  );
}

function App() {
  return (
    <AuthProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider><ModalProvider><AppContent /></ModalProvider></ThemeProvider>
      </QueryClientProvider>
    </AuthProvider>
  );
}

export default App;
